import React, { FC, useEffect, useState } from 'react';
import { FormProvider, SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { useToast } from '@common/toast';
import Icon from '@common/assets';
import BackgroundArea from '@components/profile/profileOverlay/overlays/common/backgroundArea';
import { useUserInfo } from '@repository/auth/useUserInfo';
import ExitModal from '@components/profile/profileOverlay/exitModal';
import useProfileFile, { useSaveProfileFile } from '@repository/profiles/useProfileFile';
import PortfolioAttachFiles from '@components/profile/profileOverlay/overlays/attachedFile/attachedFileForm/PortfolioAttachFiles';
import CareerAttachFiles from '@components/profile/profileOverlay/overlays/attachedFile/attachedFileForm/CareerAttachFiles';
import ReferenceUrls from '@components/profile/profileOverlay/overlays/attachedFile/attachedFileForm/ReferenceUrls';
import attachedFileSchema from '@components/profile/profileOverlay/overlays/attachedFile/attachedFileForm/attachedFileSchema';
import ProfileAttachFileSaveRq from '@domain/rq/profile/ProfileAttachFileSaveRq';
import { SourcePage } from '@domain/rq/DataLog';
import { ProfileFileFormVO } from '@domain/vo/profile/ProfileFileFormVO';
import Colors from '@domain/constant/colors';
import { KeyboardEventUtil } from '@utils/KeyboardEventUtil';

import classnames from 'classnames/bind';
import styles from './attachedFile.module.scss';
const cx = classnames.bind(styles);

const attachedFileDefaultValues: ProfileFileFormVO = {
  portfolioAttachFiles: [],
  careerAttachFiles: [],
  referenceUrls: [],
};

interface IAttachedFileOverlay {
  visible: boolean;
  onClose: () => void;
}

const AttachedFileOverlay: FC<IAttachedFileOverlay> = ({ visible, onClose }) => {
  const Toast = useToast();
  const { query } = useRouter();
  const { data: userInfo } = useUserInfo();
  const userName = userInfo?.name ?? '';
  const [exitModalVisible, setExitModalVisible] = useState(false);

  const methods = useForm<ProfileFileFormVO>({
    mode: 'onChange',
    defaultValues: attachedFileDefaultValues as ProfileFileFormVO,
    resolver: yupResolver(attachedFileSchema) as any,
  });

  const { handleSubmit, reset, formState } = methods;

  const { data: profileFile, refetch } = useProfileFile(!!userInfo?.id && visible);

  const { mutate: saveProfileFile } = useSaveProfileFile(() => {
    refetch().then(() => {
      onClose();
      Toast({ type: 'success', iconType: 'info', content: '성공적으로 저장되었습니다.' });
    });
  });

  useEffect(() => {
    if (profileFile) reset(new ProfileFileFormVO(profileFile));
  }, [profileFile]);

  const handleExit = () => {
    if (formState.isDirty) {
      setExitModalVisible(true);
      return;
    }

    onClose();
  };

  const onSubmit: SubmitHandler<ProfileFileFormVO> = async (vo) => {
    const newVo = new ProfileAttachFileSaveRq(vo);
    saveProfileFile({ ...newVo, sourcePage: query.source_page as SourcePage });
  };

  const onSubmitError: SubmitErrorHandler<ProfileFileFormVO> = () => {
    Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
  };

  return (
    <>
      <BackgroundArea visible={visible} onClickArea={handleExit} />
      <FormProvider {...methods}>
        <div className={cx('overlayArea', { on: visible })}>
          <div className={cx('overlayInner')}>
            <div className={cx('overlayTitleArea')}>
              <strong className={cx('overlayTitle')}>첨부 자료를 입력해주세요.</strong>
              <p className={cx('desc')}>
                <Icon name="informationLight" width={20} height={20} fill={Colors.C_COOL_GRAY_70} />
                구체적인 결과물은 {userName}님의 역량을 자세히 파악할 수 있도록 도와줘요.
              </p>
            </div>

            <form
              id="attachedFileForm"
              className={cx('scrollArea')}
              onSubmit={(v) => handleSubmit(onSubmit, onSubmitError)(v)}
              onKeyDown={KeyboardEventUtil.onCheckKeyDownPreventEnterExceptTextarea}
            >
              <div className={cx('scrollInner')}>
                <div className={cx('scrollWrap')}>
                  {/* 포트폴리오 */}
                  <div className={cx('infoArea')}>
                    <em className={cx('infoTitle')}>포트폴리오</em>
                    <PortfolioAttachFiles name="portfolioAttachFiles" />
                  </div>

                  {/* 경력기술서 */}
                  <div className={cx('infoArea')}>
                    <em className={cx('infoTitle')}>경력기술서</em>
                    <CareerAttachFiles name="careerAttachFiles" />
                  </div>

                  {/* URL */}
                  <div className={cx('infoArea')}>
                    <em className={cx('infoTitle')}>URL</em>
                    <ReferenceUrls name="referenceUrls" />
                  </div>
                </div>
              </div>
            </form>

            <div className={cx('btnArea')}>
              <a className={cx('btn', 'cancel')} role="button" onClick={handleExit}>
                나가기
              </a>
              <button className={cx('btn', 'confirm')} role="button" form="attachedFileForm">
                저장
              </button>
            </div>
          </div>

          {/* 나가기 모달 영역 */}
          <ExitModal visible={exitModalVisible} onClose={() => setExitModalVisible(false)} onExit={onClose} />
        </div>
      </FormProvider>
    </>
  );
};

export default AttachedFileOverlay;
